'use client';

import NextImage from 'next/image';
import { CSSProperties, forwardRef, useCallback, useEffect, useRef } from 'react';

import { BackgroundLoader } from '../BackgroundLoader/BackgroundLoader';
import { getAltFromImageSrc, toggleGallerySliderModal } from '@/utils/generic';
import gsap from 'gsap';
import { devices } from '@/utils/sizes';
import * as Styled from './Image.styled';
import { PlaceholderValue } from 'next/dist/shared/lib/get-img-props';
import { LoaderSpinner } from '../LoaderSpinner/LoaderSpinner';
import { getProxyedDirectusImageUrl } from '@/utils/page';
// import { usePageStore } from '@/store/usePageStore';

export interface TImageViewportWidths {
  mobile: string;
  tablet: string;
  desktop: string;
}

interface IImage {
  src: string;
  alt: string;
  height?: number;
  width?: number;
  showInSlider?: boolean;
  className?: string;
  classNameContainer?: string;
  lazy?: boolean;
  priority?: boolean;
  objectFit?: CSSProperties['objectFit'];
  base64?: string;
  includeContainer?: boolean;
  includeInSlider?: boolean;
  showLoaderSpinner?: boolean;
  aspectRatio?: string;
  fill?: boolean;
  styles?: CSSProperties;
  isLoader?: boolean;
  sizes?: TImageViewportWidths;
  ref?: React.Ref<HTMLImageElement>;
  fadeInOnLoad?: boolean;
  isNotDownloadable?: boolean;
  includePlayIcon?: boolean;
  sliderReference?: string;
}

type Props = Omit<IImage, 'ref'>;

export const Image = forwardRef<HTMLImageElement, Props>(function Image(
  {
    src,
    alt,
    height,
    width,
    className,
    classNameContainer,
    objectFit = 'cover',
    lazy = false,
    priority = false,
    includeContainer = false,
    includeInSlider = false,
    showLoaderSpinner = false,
    aspectRatio = 'auto',
    fill = true,
    styles,
    base64,
    sizes = {
      mobile: '100vw',
      tablet: '100vw',
      desktop: '100vw',
    },
    isLoader,
    fadeInOnLoad = true,
    includePlayIcon = false,
    isNotDownloadable = false,
    sliderReference,
    ...props
  },
  forwardedRef
) {
  // const stateSelector = usePageStore;
  // const setIsImagesGalleryDisplayed = stateSelector(
  //   (s) => s.setIsImagesGalleryDisplayed
  // );

  // merge internal + forwarded refs so both receive the <img>
  const setRefs = useCallback(
    (node: HTMLImageElement | null) => {
      imageRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLImageElement | null>).current = node;
      }
    },
    [forwardedRef]
  );

  const imgProps = {
    src: src.replaceAll('//', '/'),
    alt: alt || getAltFromImageSrc(src),
    ...(fill ? { fill: true } : { width, height }),
    ...(className && { className }),
    ...(sizes && {
      sizes: `(max-width: ${devices.max.mobile}px) ${sizes.mobile}, (max-width: ${devices.max.tablet}px) ${sizes.tablet}, (max-width: 2562px) ${sizes.desktop}`,
    }),
    style: {
      objectFit: objectFit as CSSProperties['objectFit'],
      ...styles,
      cursor: includeInSlider ? 'zoom-in' : 'inherit',
    },
    ...((base64 && !showLoaderSpinner) && { placeholder: 'blur' as PlaceholderValue, blurDataURL: base64 }),
    ...priority && { priority: true },
    loading: lazy ? 'lazy' as 'lazy' | 'eager' : 'eager' as 'lazy' | 'eager',
    ...(isNotDownloadable ? { 'data-events': 'false' } : {}),
  };

  const imageRef = useRef<HTMLImageElement | null>(null);
  const refLoaderSpinner = useRef<HTMLDivElement>(null);

  const handleImageLoad = () => {
    if (fadeInOnLoad) {
      gsap.fromTo(
        imageRef.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }
      );
    }

    if (showLoaderSpinner && refLoaderSpinner.current) {
      refLoaderSpinner.current.style.display = 'none';
    }
  };

  useEffect(() => {
    if (!showLoaderSpinner || !refLoaderSpinner.current) return;
    // Leave some margin to show the loader spinner when loading the image
    setTimeout(() => {
      if (refLoaderSpinner.current) {
        refLoaderSpinner.current.style.opacity = '1';
      }
    }, 200);

  },[showLoaderSpinner]);

  const ImgElement = (
    <>
      {showLoaderSpinner && (
        <LoaderSpinner
          isDisplayed={false}
          ref={refLoaderSpinner}
          size={'50px'}
          color={'var(--c-white-200)'}
          borderWidth="1px"
          styles={{ zIndex: -1 }}
        />
      )}

      <NextImage
        {...imgProps}
        ref={setRefs}          
        onLoad={handleImageLoad}
        // onClick={
        //   (includeInSlider && !includeContainer)
        //     ? (e) => {
        //         e.stopPropagation();
        //         e.preventDefault();
        //         toggleGallerySliderModal(sliderReference || src);
        //       }
        //     : undefined
        // }
        {...props}
        { ...src.includes(`${process.env.NEXT_PUBLIC_ADMIN_URL}/assets/`) 
          ? { overrideSrc: getProxyedDirectusImageUrl(src.split('/assets/')[1]) } : {} 
        }
        data-src={sliderReference || src}
      />
    </>
  );

  if (includeContainer) { 
    return (
      <Styled.ImageContainer 
        aspectRatio={aspectRatio || 'auto'}
        className={`image-container ${includeInSlider ? 'img-container-with-slider-toggle' : ''} ${classNameContainer || ''}`}
        data-is-slider-toggle={includeInSlider}
        onClick={
          (includeInSlider && includeContainer)
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleGallerySliderModal(sliderReference || src);
              }
            : undefined
        }
      >
        {!isLoader && src ? ImgElement : <BackgroundLoader />}
        {includePlayIcon && (
          <NextImage
            src="/img/icons/play.svg"
            className="play-icon"
            fill={false}
            alt="play icon"
            width={41 * 1.2}
            height={28 * 1.2}
          />
        )}
      </Styled.ImageContainer>
    );
  }

  return !isLoader && src ? ImgElement : <BackgroundLoader />;
});
